import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchCryptoInstructions,
  fetchCryptoPairQr,
  paymentErrorMessage,
  submitDepositReference,
  type CryptoInstructions,
  type DepositResponse,
} from '../api/payments';

type Props = {
  asset: string;
  network: string;
  deposit?: DepositResponse | null;
  onDepositUpdated?: (next: DepositResponse) => void;
  coinIcon?: ReactNode;
};

const NETWORK_LABEL: Record<string, string> = {
  TON: 'TON',
  TRC20: 'Tron',
  ERC20: 'Ethereum',
  BEP20: 'BSC',
  SOLANA: 'Solana',
  BITCOIN: 'Bitcoin',
  LTC: 'Litecoin',
};

function QrGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm10 0h2v2h-2v-2zm4 0h2v2h-2v-2zm-4 4h2v2h-2v-2zm2 2h2v2h-2v-2zm2-2h2v2h-2v-2zm-6-4h2v2h-2v-2zm8 0h2v4h-2v-2h-2v-2h2z"
      />
    </svg>
  );
}

function CopyGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M9 7h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zm-4 4H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1h-2V5H4v4h1v2z"
      />
    </svg>
  );
}

export function CryptoInstantDepositPanel({
  asset,
  network,
  deposit,
  onDepositUpdated,
  coinIcon,
}: Props) {
  const { t } = useTranslation(['payments', 'errors']);
  const titleId = useId();
  const [info, setInfo] = useState<CryptoInstructions | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportedAmount, setReportedAmount] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setInfo(null);
    setQrOpen(false);
    setFormOpen(false);
    setReportedAmount('');
    setStatusMsg(null);
    void fetchCryptoInstructions(asset, network)
      .then((row) => {
        if (!cancelled) setInfo(row);
      })
      .catch((err) => {
        if (!cancelled) setError(paymentErrorMessage(err, t('payments:cryptoUnavailable')));
      });
    return () => {
      cancelled = true;
    };
  }, [asset, network, t]);

  useEffect(() => {
    let revoked = false;
    let created: string | null = null;
    setQrUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    void fetchCryptoPairQr(asset, network)
      .then((url) => {
        created = url;
        if (revoked) URL.revokeObjectURL(url);
        else setQrUrl(url);
      })
      .catch(() => {
        if (!revoked) setQrUrl(null);
      });
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [asset, network]);

  useEffect(() => {
    if (!qrOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setQrOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [qrOpen]);

  async function onCopyAddress(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      // ignore
    }
  }

  async function onSaveQr() {
    if (!qrUrl) return;
    setSaveMsg(null);
    const filename = `vbbplay-${asset}-${network}-qr.png`;
    try {
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      const file = new File([blob], filename, { type: blob.type || 'image/png' });
      const nav = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean;
        share?: (data: ShareData) => Promise<void>;
      };
      if (nav.canShare?.({ files: [file] }) && nav.share) {
        await nav.share({ files: [file], title: t('payments:cryptoTransferTitle') });
        setSaveMsg(t('payments:cryptoQrSaved'));
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
      setSaveMsg(t('payments:cryptoQrSaved'));
    } catch {
      window.open(qrUrl, '_blank', 'noopener,noreferrer');
    }
  }

  async function onConfirmPayment() {
    if (!deposit?.public_id) {
      setStatusMsg(t('payments:cryptoOpenFirst'));
      return;
    }
    const amountRaw = reportedAmount.trim().replace(',', '.');
    const amountNum = Number.parseFloat(amountRaw);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setStatusMsg(t('payments:cryptoReportedAmountInvalid'));
      return;
    }
    const floorRaw = info?.min_crypto_amount || deposit?.crypto_amount || '';
    const minNum = Number.parseFloat(String(floorRaw).replace(',', '.'));
    const floorSymbol = info?.min_crypto_symbol || asset;
    if (Number.isFinite(minNum) && minNum > 0 && amountNum + 1e-12 < minNum) {
      setStatusMsg(t('payments:cryptoBelowMinimum', { amount: `${floorRaw} ${floorSymbol}` }));
      return;
    }
    setBusy(true);
    setStatusMsg(null);
    try {
      // Amount-only claim → pending_review. TX hash is admin-side (shared addresses).
      const updated = await submitDepositReference(deposit.public_id, null, {
        reported_crypto_amount: amountRaw,
      });
      onDepositUpdated?.(updated);
      setStatusMsg(t('payments:cryptoConfirmAccepted'));
      setFormOpen(false);
      setReportedAmount('');
    } catch (err) {
      setStatusMsg(paymentErrorMessage(err, t('errors:generic')));
    } finally {
      setBusy(false);
    }
  }

  const address = info?.address || deposit?.requisite?.details?.address || '';
  const minAmount = info?.min_crypto_amount || deposit?.crypto_amount || '—';
  const minSymbol = info?.min_crypto_symbol || asset;
  const networkLabel = NETWORK_LABEL[network] || network;
  const minDisplay = `${minAmount} ${minSymbol} (${network})`;
  const terminalStatus = Boolean(
    deposit
      && (deposit.status === 'pending_review'
        || deposit.status === 'approved'
        || deposit.status === 'completed'
        || deposit.status === 'rejected'),
  );
  const reviewing = deposit?.status === 'pending_review';
  // Always show confirm CTA on desktop+mobile while deposit is openable (not terminal).
  // Previously canConfirm required deposit?.public_id — when open-deposit lagged, the button vanished.
  const showConfirmUi = !terminalStatus;

  return (
    <section className="vbb-crypto-instant vbb-crypto-pay" aria-labelledby={titleId}>
      <header className="vbb-crypto-instant-head">
        <h3 id={titleId}>{t('payments:cryptoTransferTitle')}</h3>
      </header>

      {error ? <p className="error-note" role="alert">{error}</p> : null}

      <div className="vbb-crypto-transfer-fields" role="group" aria-label={t('payments:cryptoTransferTitle')}>
        <div className="vbb-crypto-transfer-row">
          {coinIcon ? <span className="vbb-crypto-transfer-mini-coin">{coinIcon}</span> : null}
          <div className="vbb-crypto-transfer-body">
            <small>{t('payments:cryptoMinDepositLabel')}</small>
            <strong>{minDisplay}</strong>
          </div>
        </div>

        <div className="vbb-crypto-transfer-row vbb-crypto-address-row">
          <div className="vbb-crypto-transfer-body" style={{ flex: 1, minWidth: 0 }}>
            <small>{t('payments:cryptoTransferAddress')}</small>
            <strong className="vbb-crypto-address-value">{address || t('payments:cryptoNoAddress')}</strong>
          </div>
          <button
            type="button"
            className="vbb-crypto-qr-trigger"
            disabled={!address}
            onClick={() => void onCopyAddress(address)}
            aria-label={copied ? t('payments:copied') : t('payments:copy')}
            title={copied ? t('payments:copied') : t('payments:copy')}
          >
            <CopyGlyph />
          </button>
        </div>

        <div className="vbb-crypto-transfer-row vbb-crypto-network-row">
          {coinIcon ? <span className="vbb-crypto-transfer-mini-coin">{coinIcon}</span> : null}
          <div className="vbb-crypto-transfer-body">
            <small>{t('payments:cryptoTransferNetwork')}</small>
            <strong>{networkLabel}</strong>
          </div>
          <button
            type="button"
            className="vbb-crypto-qr-trigger"
            disabled={!qrUrl}
            onClick={() => setQrOpen(true)}
            aria-label={t('payments:cryptoShowQr')}
            title={t('payments:cryptoShowQr')}
          >
            <QrGlyph />
          </button>
        </div>
      </div>

      <div className="vbb-crypto-warnings vbb-crypto-instant-warnings">
        <div className="vbb-crypto-warning">
          <span aria-hidden="true">⚠</span>
          <p>
            {asset === 'GRAM'
              ? t('payments:cryptoWarnGramNetwork')
              : t('payments:cryptoWarnNetworkGeneric', { asset, network: networkLabel })}
          </p>
        </div>
        <div className="vbb-crypto-warning">
          <span aria-hidden="true">⚠</span>
          <p>{t('payments:cryptoWarnMinGeneric', { amount: minDisplay })}</p>
        </div>
      </div>

      {showConfirmUi ? (
        <div className="vbb-crypto-confirm vbb-crypto-confirm-sticky">
          {!formOpen ? (
            <>
              <button
                type="button"
                className="btn btn-primary btn-block"
                disabled={!deposit?.public_id || !address}
                onClick={() => {
                  setFormOpen(true);
                  setStatusMsg(null);
                }}
              >
                {t('payments:cryptoConfirmPayment')}
              </button>
              {!deposit?.public_id ? (
                <p className="muted-note" role="status">{t('payments:cryptoOpenFirst')}</p>
              ) : !address ? (
                <p className="error-note" role="alert">{t('payments:cryptoNoAddress')}</p>
              ) : null}
            </>
          ) : (
            <div className="vbb-crypto-confirm-form">
              <label htmlFor="crypto-reported-amount">
                {t('payments:cryptoReportedAmountLabel', { asset: minSymbol })}
              </label>
              <input
                id="crypto-reported-amount"
                className="field-input"
                inputMode="decimal"
                value={reportedAmount}
                onChange={(e) => setReportedAmount(e.target.value)}
                placeholder={t('payments:cryptoReportedAmountPlaceholder', { asset: minSymbol })}
                disabled={busy}
                autoComplete="off"
              />
              <button
                type="button"
                className="btn btn-primary btn-block"
                disabled={busy || !reportedAmount.trim() || !deposit?.public_id}
                onClick={() => void onConfirmPayment()}
              >
                {t('payments:cryptoConfirmSend')}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-block"
                disabled={busy}
                onClick={() => setFormOpen(false)}
              >
                {t('payments:cryptoBack')}
              </button>
            </div>
          )}
        </div>
      ) : null}

      {reviewing || statusMsg ? (
        <p className="muted-note vbb-crypto-review-status" role="status">
          {statusMsg || t('payments:cryptoConfirmAccepted')}
        </p>
      ) : null}

      {qrOpen && qrUrl ? (
        <div
          className="qr-overlay vbb-crypto-qr-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${titleId}-qr`}
          onClick={(event) => {
            if (event.target === event.currentTarget) setQrOpen(false);
          }}
        >
          <div className="vbb-crypto-qr-sheet">
            <div className="vbb-crypto-qr-sheet-bar">
              <button type="button" className="cashier-back" onClick={() => setQrOpen(false)}>
                ‹ &nbsp; {t('payments:cryptoBack')}
              </button>
            </div>
            <h3 id={`${titleId}-qr`}>{t('payments:cryptoTransferTitle')}</h3>
            <img src={qrUrl} alt={t('payments:qrLabel')} width={280} height={280} />
            <button type="button" className="btn btn-primary btn-block" onClick={() => void onSaveQr()}>
              {t('payments:saveQr')}
            </button>
            {saveMsg ? (
              <p className="muted-note" role="status">
                {saveMsg}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
